import type { CardId, WallCard } from '@walls/store';
import { isClusterCard } from '../selectors/boardCardSelectors';
import { getCardRect, getClusterChildElements } from './cardRects';
import type { BoardPositionHit } from './types';
import type { BoardRect } from '../selection/utils/selectionGeometry';

export interface HitTestBoardCardsOptions {
  excludedElementId?: CardId | null;
  getTopLevel?: boolean;
  measuredRects?: Record<string, BoardRect>;
}

export function hitTestBoardCards(
  cards: WallCard[],
  xPos: number,
  yPos: number,
  options: HitTestBoardCardsOptions = {}
): BoardPositionHit | null {
  const orderedCards = [...cards].sort((a, b) => (b.zPos ?? 0) - (a.zPos ?? 0));

  for (const card of orderedCards) {
    if (options.excludedElementId && card.id === options.excludedElementId) {
      continue;
    }

    const rect = options.measuredRects?.[card.id] ?? getCardRect(card);

    if (!rect || !pointInRect(xPos, yPos, rect)) {
      continue;
    }

    if (!isClusterCard(card)) {
      if (card.width == null && card.height == null) {
        return { card, cluster: null };
      }

      continue;
    }

    if (card.collapsed) {
      continue;
    }

    if (options.getTopLevel) {
      return { card: null, cluster: card };
    }

    const childHit = hitTestChildCluster(
      card,
      xPos - rect.x,
      yPos - rect.y,
      options.excludedElementId
    );

    return {
      card: childHit.card?.id === options.excludedElementId ? null : childHit.card,
      cluster: childHit.cluster ?? card,
    };
  }

  return null;
}

export function hitTestChildCluster(
  parentCluster: WallCard,
  xPos: number,
  yPos: number,
  excludedElementId?: CardId | null
): BoardPositionHit {
  let lastPrevious: WallCard | null = null;
  let lastPreviousRect: { height: number; width: number; x: number; y: number } | null = null;

  for (const childElement of getClusterChildElements(parentCluster)) {
    const childCard = childElement.card;

    if (!childCard) {
      continue;
    }

    if (excludedElementId && childCard.id === excludedElementId) {
      continue;
    }

    const rect = childElement.rect;

    if (pointInRect(xPos, yPos, rect)) {
      if (isClusterCard(childCard) && !childCard.collapsed) {
        return hitTestChildCluster(childCard, xPos - rect.x, yPos - rect.y, excludedElementId);
      }

      return { card: childCard, cluster: parentCluster };
    }

    if (!parentCluster.width && xPos > rect.x && xPos < rect.x + rect.width) {
      if (
        (lastPreviousRect == null && yPos > rect.y + rect.height) ||
        (lastPreviousRect != null &&
          yPos > rect.y + rect.height &&
          rect.y + rect.height > lastPreviousRect.y + lastPreviousRect.height)
      ) {
        lastPrevious = childCard;
        lastPreviousRect = rect;
      }
    }
  }

  return { card: lastPrevious, cluster: parentCluster };
}

function pointInRect(
  xPos: number,
  yPos: number,
  rect: { height: number; width: number; x: number; y: number }
) {
  return xPos > rect.x && xPos < rect.x + rect.width && yPos > rect.y && yPos < rect.y + rect.height;
}
